import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { NewsletterAudienceUser } from '../types/models';
import { ApiResponseNewsletterAudience } from '../types/api/ApiResponseNewsletterAudience';

interface UseNewsletterAudienceReturn {
  users: NewsletterAudienceUser[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches the newsletter **audience** — every user across all tenants, each
 * annotated with their resolved `newsletter_opt_in` flag — for the
 * Ancalagon-staff-only Newsletter admin page (`GET /admin/newsletters/users`).
 *
 * The endpoint returns the FULL list in one call (no server-side pagination or
 * search parameters), so consumers filter and paginate client-side.
 *
 * Per the API contract the backend wraps responses in JSend and maps failures
 * to `status: 'error'` at HTTP 200 (a non-staff caller even receives a JSend
 * `fail`), so this branches on `body.status === 'success'` — never
 * `response.ok`, which can be `true` on a logical failure.
 */
export const useNewsletterAudience = (): UseNewsletterAudienceReturn => {
  const [users, setUsers] = useState<NewsletterAudienceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchAudience = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/admin/newsletters/users');
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterAudience | null;

      if (body && body.status === 'success' && Array.isArray(body.data?.users)) {
        setUsers(body.data.users);
      } else {
        throw new Error(body?.message ?? 'Failed to load the newsletter audience.');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load the newsletter audience.'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchAudience();
    }
  }, [auth.isAuthenticated, fetchAudience]);

  return { users, loading, error, refetch: fetchAudience };
};

export default useNewsletterAudience;
